"use client";

import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      // Same lighter scrim as DialogOverlay (bg-black/40, was /80) so a side panel and a centred
      // modal darken the page identically. Override per instance via SheetContent `overlayClassName`.
      "fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-white p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      // Each side names `border-outlines` EXPLICITLY. The §9.3(a) base-layer default already makes
      // a bare `border-l` correct, but a global default is invisible here — naming the token keeps
      // this file self-documenting and immune to a future base-layer change. The `right` variant is
      // the Sources panel, whose near-black left edge is the reported defect.
      //
      // `right` is the form-panel side (SourcesSheet, the weekly-report panel) and is standardised
      // on `sm:max-w-xl` — the wider of the two widths that were previously set per-callsite
      // (`sm:max-w-md` vs `sm:max-w-xl`), because these panels host forms and need the room. Set
      // the width HERE rather than at each callsite so the two panels cannot drift apart again.
      side: {
        top: "inset-x-0 top-0 border-b border-outlines data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t border-outlines data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r border-outlines data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l border-outlines data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-xl",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /**
   * Render the dimming overlay behind the panel. Default `true`.
   *
   * Set `false` for a NON-MODAL sheet (`<Sheet modal={false}>`). `SheetOverlay` is
   * `fixed inset-0`, so even with Radix's modal behaviour disabled it still covers the viewport and
   * swallows every pointer event outside the panel — which silently defeats `modal={false}` and
   * makes the surface behind it unclickable. A non-modal sheet must therefore omit it.
   *
   * Introduced for Phase F1's `SourcesSheet`, which has to stay open while the user keeps typing in
   * the project chat (CR §3.4: "Non-modal floating panel — stays open alongside work").
   */
  overlay?: boolean;
  /**
   * Extra classes for the dimming overlay (e.g. `bg-black/20` for a lighter scrim). Merged with
   * `cn`, so a background utility here replaces the default `bg-black/40`. Ignored when
   * `overlay={false}`.
   */
  overlayClassName?: string;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      className,
      children,
      overlay = true,
      overlayClassName,
      ...props
    },
    ref,
  ) => (
    <SheetPortal>
      {overlay && <SheetOverlay className={overlayClassName} />}
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
        <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-subtle-bg">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

/**
 * `pr-8` is a DEFAULT, not a per-callsite concern: `SheetContent` always renders an
 * `absolute right-4 top-4` close button, so every header in every sheet needs the same clearance or
 * its title collides with the X. It was previously set at one callsite and forgotten at the other.
 * Override it only for a header that is `sr-only` or genuinely full-width.
 */
const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 pr-8 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

/**
 * Defaults to the `text-section-heading` design token (18px / 1.4 / 600 — see globals.css) rather
 * than raw `text-lg font-semibold`. A sheet title IS a top-level section title, and the token was
 * already being applied by hand at one callsite and not the other. The token carries its own
 * font-weight, so no separate `font-semibold` is needed; `cn`'s typography-scale handling in
 * lib/utils.ts still lets a callsite override it with another scale token.
 */
const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-section-heading text-text-primary", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-text-secondary", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
